import { normalizeForMatch } from './routing.ts';

/**
 * The staff command bar: turns typed text (English or Arabic) into things the app can do. Deterministic and
 * local: nothing typed here leaves the browser except the resulting API calls. Results are suggestions; the
 * person picks one, and anything that changes data says exactly what it will do first.
 */
export type Page =
  | 'home'
  | 'inbox'
  | 'people'
  | 'companies'
  | 'deals'
  | 'cases'
  | 'tasks'
  | 'calendar'
  | 'settings'
  | 'teams'
  | 'integrations'
  | 'users'
  | 'audit';

export type Due = { days: number } | null;

export type Intent =
  | { kind: 'navigate'; page: Page }
  | { kind: 'createTask'; title: string; due: Due }
  | { kind: 'createCase'; subject: string }
  | { kind: 'conversation'; action: 'assignToMe' | 'close' | 'reopen' }
  | { kind: 'status'; status: 'available' | 'away' }
  | { kind: 'theme'; theme: 'light' | 'dark' | 'auto' }
  | { kind: 'language'; locale: 'en' | 'ar' }
  // Setting work up by asking (typed or spoken through LY Voice): prepared for the person to confirm.
  | { kind: 'automation'; recipe: string; set: { enabled?: boolean; mode?: 'auto' | 'manual' } }
  | { kind: 'assistantSwitch'; on: boolean }
  | { kind: 'followUp'; about: string; days: number }
  | { kind: 'post'; text: string; due: Due }
  | { kind: 'search'; query: string };

export const PAGE_PATHS: Record<Page, string> = {
  home: '/home',
  inbox: '/inbox',
  people: '/people',
  companies: '/companies',
  deals: '/deals',
  cases: '/cases',
  tasks: '/tasks',
  calendar: '/calendar',
  settings: '/settings',
  teams: '/admin/teams',
  integrations: '/admin/integrations',
  users: '/admin/users',
  audit: '/admin/audit',
};

const PAGE_WORDS: Record<Page, string[]> = {
  home: ['home', 'dashboard', 'الرئيسية', 'لوحة التحكم'],
  inbox: ['inbox', 'messages', 'conversations', 'chats', 'الوارد', 'صندوق الوارد', 'المحادثات', 'الرسائل'],
  people: ['people', 'contacts', 'customers', 'الأشخاص', 'جهات الاتصال', 'العملاء'],
  companies: ['companies', 'accounts', 'الشركات', 'الحسابات'],
  deals: ['deals', 'pipeline', 'sales', 'الصفقات', 'المبيعات'],
  cases: ['cases', 'tickets', 'complaints', 'الحالات', 'التذاكر', 'الشكاوى'],
  tasks: ['tasks', 'to do', 'todo', 'my tasks', 'المهام', 'مهامي'],
  calendar: ['calendar', 'holidays', 'time off', 'leave', 'التقويم', 'العطلات', 'الإجازات'],
  settings: ['settings', 'preferences', 'service levels', 'sla', 'الإعدادات', 'مستويات الخدمة'],
  teams: ['teams', 'routing', 'routing rules', 'الفرق', 'التوجيه'],
  integrations: ['integrations', 'channels', 'whatsapp settings', 'التكاملات', 'القنوات', 'الربط'],
  users: ['users', 'staff', 'المستخدمون', 'المستخدمين', 'الموظفون', 'الموظفين'],
  audit: ['audit', 'audit log', 'سجل التدقيق'],
};

const n = normalizeForMatch;
const NAV_PREFIX =
  /^(go to|open|show( me)?|take me to|view|navigate to|افتح|اذهب الى|اذهب|اعرض|انتقل الى|روح)\s+/;
const TASK_PREFIX =
  /^(new task|add (a )?task|create (a )?task|task|remind me to|todo|مهمه جديده|اضف مهمه|انشئ مهمه|مهمه|ذكرني)( |$)/;
const CASE_PREFIX =
  /^(new case|open a case|create (a )?case|log a complaint|new complaint|حاله جديده|افتح حاله|شكوي جديده|سجل شكوي)( |$)/;

/**
 * Words of the original text with their normalised forms. Normalising never splits or joins words, so the two
 * stay aligned, and phrases found in the normalised text can be cut from the original by word position.
 */
function words(original: string) {
  const orig = original.split(' ').filter(Boolean);
  return { orig, norm: orig.map((w) => n(w)) };
}

/** Index range of the words covered by a regex match on the space-joined normalised words. */
function wordSpan(norm: string[], re: RegExp): { from: number; to: number; m: RegExpMatchArray } | null {
  const joined = norm.join(' ');
  const m = joined.match(re);
  if (!m || m.index === undefined) return null;
  const start = m.index;
  const end = start + m[0].trimEnd().length;
  let pos = 0;
  let from = -1;
  let to = -1;
  norm.forEach((w, k) => {
    const a = pos;
    const b = pos + w.length;
    if (from < 0 && b > start) from = k;
    if (a < end) to = k;
    pos = b + 1;
  });
  return from < 0 ? null : { from, to, m };
}

const DUE_RULES: [RegExp, (m: RegExpMatchArray) => number][] = [
  [/\b(today|tonight)\b|اليوم|الليله/, () => 0],
  [/\bday after tomorrow\b|بعد غد/, () => 2],
  [/\b(tomorrow|tmrw)\b|غدا|بكره|بكرا/, () => 1],
  [/\bnext week\b|الاسبوع القادم|الاسبوع الجاي/, () => 7],
  [/\bin (\d{1,3}) days?\b/, (m) => Number(m[1])],
  [/بعد (\d{1,3}) (ايام|يوم)/, (m) => Number(m[1])],
];

/** Splits "call Fatima tomorrow" into a title ("call Fatima") and when it is due (in days from today). */
function extractDue(orig: string[], norm: string[]): { title: string; due: Due } {
  for (const [re, days] of DUE_RULES) {
    const span = wordSpan(norm, re);
    if (!span) continue;
    let from = span.from;
    // Drop a connector right before the date ("by tomorrow", "on Monday", "يوم").
    if (from > 0 && /^(by|on|due|في|يوم)$/.test(norm[from - 1]!)) from--;
    const title = [...orig.slice(0, from), ...orig.slice(span.to + 1)].join(' ');
    return { title, due: { days: days(span.m) } };
  }
  return { title: orig.join(' '), due: null };
}

function pageFor(text: string): Page | null {
  for (const [page, words] of Object.entries(PAGE_WORDS) as [Page, string[]][])
    if (words.some((w) => n(w) === text)) return page;
  return null;
}

/**
 * Suggestions for what the person typed, best first. `inConversation` enables actions on the open conversation.
 * Titles keep the original casing/script; matching uses a normalised copy.
 */
export function parseCommand(raw: string, ctx: { inConversation: boolean }): Intent[] {
  const original = raw.trim().replace(/\s+/g, ' ');
  const text = n(original);
  if (!text) return [];
  const out: Intent[] = [];

  // Create a task / case: keep the person's own words (script and casing) for the title.
  const w = words(original.replace(/^(\S+)\s*[:\-–]\s*/, '$1 '));
  const task = w.norm.join(' ').match(TASK_PREFIX);
  if (task) {
    const skip = task[1]!.split(' ').length;
    const { title, due } = extractDue(w.orig.slice(skip), w.norm.slice(skip));
    if (title) out.push({ kind: 'createTask', title: title.charAt(0).toUpperCase() + title.slice(1), due });
  }
  const kase = w.norm.join(' ').match(CASE_PREFIX);
  if (kase && ctx.inConversation) {
    const subject = w.orig.slice(kase[1]!.split(' ').length).join(' ');
    out.push({ kind: 'createCase', subject });
  }

  // Navigation: "open deals", "افتح المهام", or just a page name.
  const nav = pageFor(
    text
      .replace(NAV_PREFIX, '')
      .replace(/^(the|my) /, '')
      .trim(),
  );
  if (nav) out.push({ kind: 'navigate', page: nav });

  if (ctx.inConversation) {
    if (
      /\b(assign( it)? to me|take (it|this)|i'?ll take (it|this))\b|اسند(ها)? (الي|لي)|خذها|ساخذها/.test(text)
    )
      out.push({ kind: 'conversation', action: 'assignToMe' });
    if (/^(close|resolve|done)( (it|this|conversation|chat))?$|^(اغلق|اغلاق)( المحادثه)?$/.test(text))
      out.push({ kind: 'conversation', action: 'close' });
    if (/^(re-?open)( (it|this|conversation))?$|^اعد فتح/.test(text))
      out.push({ kind: 'conversation', action: 'reopen' });
  }

  // A request to set something up ("switch win back to manual") is not a status change ("I'm back").
  const workflow = workflowIntents(original, text);
  if (!workflow.length) {
    if (
      /\b(i'?m |set me |mark me |status )?(away|busy|on break|offline)\b|غير متاح|مشغول|في استراحه/.test(text)
    )
      out.push({ kind: 'status', status: 'away' });
    else if (/\b(i'?m |set me |mark me |status )?(available|back|online)\b|^متاح$|انا متاح|رجعت/.test(text))
      out.push({ kind: 'status', status: 'available' });

    if (/\bdark( mode| theme)?\b|الوضع الداكن|داكن|ليلي/.test(text))
      out.push({ kind: 'theme', theme: 'dark' });
    else if (/\blight( mode| theme)?\b|الوضع الفاتح|فاتح|نهاري/.test(text))
      out.push({ kind: 'theme', theme: 'light' });
    else if (/\b(auto|system)( mode| theme)\b|تلقائي/.test(text)) out.push({ kind: 'theme', theme: 'auto' });
  }

  out.push(...workflow);

  if (/\b(arabic|in arabic)\b|عربي|العربيه/.test(text)) out.push({ kind: 'language', locale: 'ar' });
  else if (/\b(english|in english)\b|انجليزي|الانجليزيه|انكليزي/.test(text))
    out.push({ kind: 'language', locale: 'en' });

  // Anything else (and everything, as a fallback) is a search across people, companies and cases.
  if (original.length >= 2 && !out.some((i) => i.kind === 'createTask'))
    out.push({ kind: 'search', query: original });
  return out;
}

// ------------------------------------------------------------------------------------------- setting work up

/** Ready-made automations by the words people use for them (English and Gulf Arabic). */
const RECIPE_WORDS: Record<string, string[]> = {
  order_confirmation: [
    'order confirmation',
    'order confirmations',
    'confirm orders',
    'تاكيد الطلب',
    'تاكيد الطلبات',
  ],
  shipping_update: [
    'shipping update',
    'shipping updates',
    'shipping',
    'on its way',
    'الشحن',
    'الطلب في الطريق',
  ],
  review_request: [
    'review request',
    'review requests',
    'ask for reviews',
    'reviews',
    'طلب تقييم',
    'التقييمات',
  ],
  abandoned_checkout: [
    'abandoned checkout',
    'abandoned cart',
    'abandoned carts',
    'cart reminder',
    'السله المتروكه',
    'السله',
  ],
  back_in_stock: ['back in stock', 'restock', 'عوده المخزون', 'رجع المخزون'],
  refill_reminder: ['refill reminder', 'refill', 'refills', 'replenish', 'تذكير التعبئه', 'اعاده التعبئه'],
  birthday_offer: ['birthday offer', 'birthday', 'birthdays', 'عيد الميلاد', 'اعياد الميلاد'],
  win_back: ['win back', 'winback', 'win-back', 'lapsed customers', 'استرجاع العملاء'],
  ad_lead_followup: ['ad lead', 'ad leads', 'lead follow up', 'عملاء الاعلانات'],
};
const ON = /\b(turn on|switch on|enable|start|activate|resume)\b|شغل|فعل|تشغيل/;
const OFF = /\b(turn off|switch off|disable|stop|pause|deactivate)\b|اوقف|عطل|ايقاف|وقف/;
const MANUAL = /\b(manual|approval|approve (each|every|them)|ask me first)\b|يدوي|بموافقه|موافقتي/;
const AUTO = /\b(automatic|automatically|auto)\b|تلقائي/;

function workflowIntents(original: string, text: string): Intent[] {
  const out: Intent[] = [];
  const recipe = Object.entries(RECIPE_WORDS).find(([, ws]) =>
    ws.some((w) => ` ${text} `.includes(` ${n(w)} `)),
  )?.[0];
  if (recipe) {
    const set: { enabled?: boolean; mode?: 'auto' | 'manual' } = {};
    if (MANUAL.test(text)) set.mode = 'manual';
    else if (AUTO.test(text) && !ON.test(text)) set.mode = 'auto';
    if (OFF.test(text)) set.enabled = false;
    else if (ON.test(text) || set.mode) set.enabled = true;
    if (set.enabled !== undefined || set.mode) out.push({ kind: 'automation', recipe, set });
  }
  if (/\b(ai )?(assistant|bot|chatbot)\b|المساعد|البوت/.test(text) && !recipe) {
    if (OFF.test(text)) out.push({ kind: 'assistantSwitch', on: false });
    else if (ON.test(text)) out.push({ kind: 'assistantSwitch', on: true });
  }
  // "follow up with everyone who asked about Eros and didn't buy", "تابع اللي سالوا عن ايروس"
  const f =
    /\bfollow ?up\b.*?\b(?:asked|enquired|inquired|messaged|wrote) (?:us )?(?:about|for) (.+?)(?:\s+(?:and|but) (?:did ?n[o']?t|never|haven'?t|have not) (?:buy|bought|order|ordered|purchase).*)?(?:\s+(?:in|over) the last (\d{1,3}) days?)?$/.exec(
      text,
    ) ??
    /تابع.*?(?:سالوا|سال|استفسروا|سالو) عن (.+?)(?:\s+(?:وما|ولا|ولم) (?:اشتروا|طلبوا|اشترو).*)?$/.exec(text);
  if (f?.[1]) {
    out.push({ kind: 'followUp', about: cleanAbout(f[1], original), days: f[2] ? Number(f[2]) : 30 });
  }
  // "post 'National Day: 20% off' everywhere tomorrow", "انشر ... على كل المنصات"
  const p =
    /^(post|publish|انشر)\s+(.+?)\s+(?:to |on |في |علي )?(?:all platforms|everywhere|every platform|all accounts|كل المنصات|جميع المنصات|كل الحسابات)(.*)$/.exec(
      text,
    );
  if (p) {
    const w = words(original);
    const start = 1;
    const end = start + p[2]!.split(' ').length;
    const body = w.orig
      .slice(start, end)
      .join(' ')
      .replace(/^["“'«]|["”'»]$/g, '');
    const rest = words(p[3]!.trim());
    out.push({
      kind: 'post',
      text: body,
      due: rest.norm.length ? extractDue(rest.orig, rest.norm).due : null,
    });
  }
  return out;
}

/** The product words as the person wrote them (casing, script), from the normalised match. */
function cleanAbout(matched: string, original: string): string {
  const w = words(original);
  const target = matched.split(' ');
  for (let i = 0; i + target.length <= w.norm.length; i++)
    if (target.every((t, k) => w.norm[i + k] === t))
      return w.orig
        .slice(i, i + target.length)
        .join(' ')
        .replace(/[?.!؟]+$/, '');
  return matched;
}

/**
 * Opt-out / opt-in keywords a customer can send (the whole message, any case, English or Arabic). Deliberately
 * narrow: "cancel" or "إلغاء" alone usually means a booking, not marketing, so they are not keywords.
 */
export function consentKeyword(text: string | null | undefined): 'withdrawn' | 'granted' | null {
  const t = normalizeForMatch(text ?? '')
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .trim();
  if (
    /^(stop|stop all|unsubscribe|opt out|optout|stop messages|الغاء الاشتراك|ايقاف|توقف|ايقاف الرسائل|لا ارغب في الرسائل)$/.test(
      t,
    )
  )
    return 'withdrawn';
  if (/^(start|subscribe|opt in|optin|اشتراك|ابدا|اشترك)$/.test(t)) return 'granted';
  return null;
}
